import { ClickAwayListener } from '@mui/material';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useIntlBds } from '../../translations/BdsIntlProvider';
import { useDeepCompare } from '../../utils';
import { Keys } from '../../utils/keysUtil';
import { styled } from '../../styles';
import { Box } from '../Box';
import { InputChip } from '../Chip/types';
import { IconButton } from '../IconButton';
import { mergeClassNames } from '../../utils/cssClassNameHelper';
import { StyledAutocompleteInput } from '../Select/SelectV2Elements/elements/Input';
import { MenuItem } from '../Menu';
import { usePopover } from '../Popover/usePopover';
import { Divider } from '../Divider/Divider';
import { Paper } from '../Paper/Paper';
import { Popper } from '../Popper';
import { Instance as PopperInstance } from '@popperjs/core';
import { CircularProgress } from '../Progress/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import { Chips } from '../Select/SelectV2Elements/elements/Chips';
import { CheckBoxTreeVirtualized } from './CheckBoxTreeVirtualized';
import { getChipList, getDropdownWidth, getGroupedChips, getNodesWithMatch } from './CheckBoxTreeSelectUtils';
import { NODE_ITEM_HEIGHT } from './StyledCheckboxTreeElements';
import { CheckBoxTreeSelectProps, Node } from './types';
import { getCheckBoxTreeTotalHeight } from './CheckBoxTreeUtils';

export const CheckBoxTreeSelect: FC<React.PropsWithChildren<CheckBoxTreeSelectProps>> = ({
  dataTest,
  children,
  closeOnChildrenClick,
  label,
  disabled,
  fullWidth,
  nodes = [],
  placement,
  maxChips,
  groupSelected,
  style,
  popoverStyle,
  placeholder,
  loading,
  required,
  helperText,
  error,
  errorText,
  sx,
  className,
  ...treeProps
}) => {
  const popperRef = useRef<PopperInstance | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const muiInputRef = useRef<HTMLDivElement>(null);
  const [chipList, setChipList] = useState<InputChip[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusOnChips, setFocusOnChips] = useState(false);
  const [filteredNodes, setFilteredNodes] = useState<Node[]>([]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [noNodes, setNoNodes] = useState<boolean>(false);
  const popover = usePopover();
  const newChecked = useDeepCompare(treeProps.checked);
  const { intl } = useIntlBds();

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setChipList(getChipList(intl.CheckboxTree.unknownLabel, nodes ?? [], treeProps.checked ?? []));
    }
    return () => {
      mounted = false;
    };
  }, [nodes, newChecked]);

  useEffect(() => {
    let mounted = true;
    const { nodes: nodesWithMatch, matchedNodes } = getNodesWithMatch(nodes ?? [], searchTerm);
    if (mounted) {
      if (searchTerm) {
        setExpanded(matchedNodes);
        setNoNodes(matchedNodes.length === 0);
      } else {
        setExpanded([]);
        setNoNodes(false);
      }
      setFilteredNodes(nodesWithMatch);
    }
    return () => {
      mounted = false;
    };
  }, [nodes, searchTerm]);

  const closeDropdown = (event?: { stopPropagation?: () => void }) => {
    popover.close(event);
    setSearchTerm('');
  };

  const deleteItems = (values: string[]) => {
    treeProps.onCheck?.(treeProps.checked?.filter((item) => !values.includes(item)) ?? []);
  };

  const childrenClickIntercept = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (closeOnChildrenClick) {
      closeDropdown(event);
    }
  };

  const handleChangeOnInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    openDropdown({ currentTarget: inputRef.current! });
    setSearchTerm(event.target.value);
  };

  const openDropdown = (event: {
    currentTarget: HTMLElement | (EventTarget & Element);
    relatedTarget?: EventTarget | Element | null;
  }) => {
    if (!event.relatedTarget || !!(event.relatedTarget as Element)?.closest('[data-test="muiSelect-close-icon"]')) {
      popover.toggle(event as any);
    }
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  const handlePopperBlur = (event: React.FocusEvent) => {
    if (
      event.relatedTarget &&
      !popperRef.current?.state?.elements?.popper?.contains(event.relatedTarget as Element) &&
      event.relatedTarget !== inputRef.current &&
      !(event.relatedTarget as Element).classList?.contains('MuiDrawer-paper') // MUI bug if inside Drawer. If you check some value in a tree and then you click it again Drawer gets focused and popper gets closed
    ) {
      closeDropdown(event);
    }
  };

  const handleKeyDownOnInput = (event: React.KeyboardEvent) => {
    if (Keys.oneOf(event, 'Tab')) {
      closeDropdown(event);
    }
    if (Keys.oneOf(event, 'Backspace') && !searchTerm && treeProps.checked?.length) {
      treeProps.onCheck?.(treeProps.checked.slice(0, -1));
    }
    if (Keys.oneOf(event, 'ArrowDown', 'ArrowUp', 'Enter')) {
      if (popover.open) {
        (popperRef?.current?.state?.elements?.popper?.querySelector('[tabindex]') as HTMLElement)?.focus();
      } else {
        openDropdown({ currentTarget: inputRef.current! });
      }
    }
  };

  const handleKeyDownOnPopper = (event: React.KeyboardEvent) => {
    if (Keys.oneOf(event, 'Escape', 'Tab')) {
      closeDropdown();
      focusInput();
    }
  };

  const fullHeight = loading
    ? NODE_ITEM_HEIGHT
    : getCheckBoxTreeTotalHeight(searchTerm ? filteredNodes : nodes, treeProps.keepExpanded, expanded);

  const minHeight = Math.min(fullHeight, 300);

  const inputWidth = getDropdownWidth(muiInputRef.current);

  const content = (
    <Paper
      elevation={2}
      style={{
        width: inputWidth
      }}
    >
      {!!children && (
        // biome-ignore lint/a11y/noStaticElementInteractions: This div is used for intercepting clicks on children and has an onClick handler.
        <div onClick={childrenClickIntercept}>
          {children}
          <Divider />
        </div>
      )}
      <StyledTreeContainer style={{ minHeight, paddingLeft: treeProps.keepExpanded ? 12 : 0 }}>
        {loading ? (
          <StyledExtraMenuItem disabled>{intl.CheckboxTree.loadingLabel}</StyledExtraMenuItem>
        ) : !noNodes ? (
          <CheckBoxTreeVirtualized
            {...treeProps}
            onExpand={(toExpand: any) => setExpanded(toExpand)}
            nodes={filteredNodes}
            dataTest={`${dataTest ?? 'checkBox'}-tree`}
            expanded={expanded}
            searchTerm={searchTerm}
          />
        ) : (
          <StyledExtraMenuItem disabled>{intl.CheckboxTree.noResultsLabel}</StyledExtraMenuItem>
        )}
      </StyledTreeContainer>
    </Paper>
  );

  const popperTarget = muiInputRef.current?.querySelector('.MuiInputBase-root');

  const chips = groupSelected && nodes ? getGroupedChips(chipList, nodes) : chipList;

  return (
    <ClickAwayListener onClickAway={closeDropdown}>
      <StyledInputWrapper data-test={dataTest} className={className}>
        <StyledAutocompleteInput
          dataTest={`${dataTest ?? 'checkbox'}-input`}
          label={label}
          sx={sx}
          className={mergeClassNames('input', {
            'Mui-focused': popover.open || focusOnChips
          })}
          error={error}
          errorText={errorText}
          required={required}
          helperText={helperText}
          disabled={disabled}
          placeholder={placeholder}
          onChange={handleChangeOnInput}
          value={searchTerm}
          onKeyDown={handleKeyDownOnInput}
          onMouseDown={(event: React.MouseEvent) => {
            if ((event.target as Element).closest('[data-test="muiSelect-close-icon"]')) {
              event.stopPropagation();
              event.preventDefault();
              return;
            }
            if (!disabled) {
              openDropdown(event);
            }
          }}
          ref={muiInputRef}
          inputRef={inputRef}
          type="text"
          autoComplete="off"
          style={style}
          fullWidth={fullWidth}
          startAdornment={
            chips.length ? (
              <InputAdornment position="start" className="multi-selection">
                <Chips
                  isFocused={popover.open || focusOnChips}
                  selectedOptions={chips.map((chip) => {
                    return { value: 'value' in chip ? chip.value : chip.values, label: chip.label?.toString() ?? '' };
                  })}
                  visibleElementsWhenSelected={{ label: true }}
                  limit={{
                    count: maxChips ?? 3
                  }}
                  disabled={disabled}
                  loading={loading}
                  onClick={focusInput}
                  onWrapperFocus={() => {
                    setFocusOnChips(true);
                  }}
                  onWrapperBlur={() => {
                    setFocusOnChips(false);
                  }}
                  onDelete={(option) => deleteItems(Array.isArray(option.value) ? option.value : [option.value])}
                />
              </InputAdornment>
            ) : undefined
          }
          endAdornment={
            <StyledEndInputAdornment position="end" onClick={focusInput}>
              {!!loading && (
                <CircularProgress
                  dataTest="loading"
                  color="inherit"
                  size={20}
                  className="loading"
                  style={{ marginRight: !chips.length ? 33 : undefined }}
                />
              )}
              {!!chips.length && !disabled && (
                <IconButton
                  iconName="close_small"
                  className="clear-icon"
                  dataTest="muiSelect-close-icon"
                  onClick={() => {
                    treeProps.onCheck?.([]);
                  }}
                  tabIndex={-1}
                />
              )}
              <StyledOpenButton
                iconName="arrow_drop_down"
                style={{ transform: popover.open ? 'rotate(180deg)' : undefined }}
                disabled={disabled}
              />
            </StyledEndInputAdornment>
          }
        />
        <Popper
          open={popover.open}
          anchorEl={popperTarget}
          role="presentation"
          placement={placement}
          popperRef={popperRef}
          disablePortal
          onBlur={handlePopperBlur}
          onKeyDown={handleKeyDownOnPopper}
          style={{ zIndex: 1301, ...(popoverStyle ?? {}) }}
        >
          {content}
        </Popper>
      </StyledInputWrapper>
    </ClickAwayListener>
  );
};

const StyledOpenButton = styled(IconButton)`
  &.MuiIconButton-root {
    padding: ${({ theme }) => theme.spacingSize.ml};
    margin-right: -2px;
    svg {
      color: rgba(0, 0, 0, 0.54) !important;
    }
  }
`;

const StyledTreeContainer = styled('div')`
  max-height: 300px;
  overflow: hidden auto;
  position: relative;
`;

const StyledEndInputAdornment = styled(InputAdornment)`
  && {
    grid-area: caret;
  }
`;

const StyledInputWrapper = styled(Box)`
  && {
    .clear-icon {
      padding: 4px;
      opacity: 0;
    }
    &:hover {
      .clear-icon {
        opacity: 1;
      }
    }
  }
`;

const StyledExtraMenuItem = styled(MenuItem)`
  && {
    padding: 14px 16px;
    color: rgba(0, 0, 0, 0.54) !important;
    opacity: 1;
  }
`;
